/** Enhance the existing shell swap and deliberate full-document listing visits. */
(function initializeListingNavigationExperience(window, document) {
  'use strict';

  if (window.TPGListingNavigationExperience) return;

  const ROSTER_PATH = '/listings/workspaces/';
  const TITLE_NAME = 'tpg-record-title';
  const nonce = document.currentScript?.nonce || '';
  let activeTransition = null;
  let prefetchUsed = false;
  let speculationScript = null;

  /** Accept exact same-origin read URLs without query, fragment, or credentials. */
  function readUrl(value) {
    try {
      const url = new URL(value, window.location.href);
      return url.origin === window.location.origin &&
        !url.username && !url.password && !url.search && !url.hash
        ? url
        : null;
    } catch (_urlError) {
      return null;
    }
  }

  /** Identify one roster/detail pair without broadening the shell's route policy. */
  function workspacePair(fromUrl, toUrl) {
    const from = readUrl(fromUrl);
    const to = readUrl(toUrl);
    if (!from || !to) return null;
    const detail = from.pathname === ROSTER_PATH ? to : from;
    const roster = from.pathname === ROSTER_PATH ? from : to;
    const match = detail.pathname.match(/^\/listings\/workspaces\/([1-9]\d*)\/$/);
    return roster.pathname === ROSTER_PATH && match
      ? { id: match[1], fromRoster: from.pathname === ROSTER_PATH }
      : null;
  }

  /** Find exactly one heading, avoiding duplicate transition-name snapshots. */
  function findTitle(id, roster) {
    const selector = roster
      ? `[data-tpg-workspace-id="${id}"] [data-tpg-workspace-title]`
      : '[data-page-module="listing-workspace"] h1';
    const titles = document.querySelectorAll(selector);
    return titles.length === 1 ? titles[0] : null;
  }

  /** Run the shell-owned update once; native animation never becomes navigation. */
  function swap({ fromUrl, toUrl, update }) {
    const pair = workspacePair(fromUrl, toUrl);
    if (
      !pair || typeof document.startViewTransition !== 'function' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return Promise.resolve().then(update);
    }

    if (activeTransition) {
      activeTransition.transition.skipTransition();
      activeTransition.cleanup();
    }

    const namedTitles = [];
    function nameTitle(title) {
      if (!title) return;
      namedTitles.push({ title, previous: title.style.viewTransitionName });
      title.style.viewTransitionName = TITLE_NAME;
    }
    function cleanup() {
      namedTitles.splice(0).forEach(({ title, previous }) => {
        if (title.style.viewTransitionName === TITLE_NAME) {
          title.style.viewTransitionName = previous;
        }
      });
    }
    nameTitle(findTitle(pair.id, pair.fromRoster));

    let updatePromise;
    function applyUpdate() {
      if (!updatePromise) {
        updatePromise = Promise.resolve().then(update).then(() => {
          nameTitle(findTitle(pair.id, !pair.fromRoster));
        });
      }
      return updatePromise;
    }

    let transition;
    try {
      transition = document.startViewTransition(applyUpdate);
    } catch (_transitionError) {
      // A browser refusing animation must still perform the original update.
      return applyUpdate().finally(cleanup);
    }
    const active = { transition, cleanup };
    activeTransition = active;
    transition.ready.catch(() => {});
    const finished = () => {
      cleanup();
      if (activeTransition === active) activeTransition = null;
    };
    transition.finished.then(finished, finished);
    return transition.updateCallbackDone;
  }

  /** Remove pending document hints before app state changes; never reuse them. */
  function clearPrefetch() {
    if (!speculationScript) return;
    speculationScript.remove();
    speculationScript = null;
    prefetchUsed = true;
  }

  /** Prepare only Team Listings on a deliberate cross-owner document visit. */
  function prefetchOnIntent(event) {
    const link = event.target?.closest?.('a[data-tpg-prefetch="listing-read"]');
    if (
      !link || prefetchUsed || document.visibilityState !== 'visible' ||
      window.navigator.connection?.saveData || window.navigator.onLine === false ||
      link.hasAttribute('download') || (link.target && link.target !== '_self') ||
      !window.HTMLScriptElement.supports?.('speculationrules')
    ) return;

    const url = readUrl(link.href);
    const context = window.TPGPersistentShell?.getCurrentPageContext?.();
    if (
      !url || url.pathname !== '/listings/' || !context?.pageOwner ||
      context.pageOwner === 'listings' ||
      window.location.pathname.startsWith('/listings/')
    ) return;

    // The native document cache cannot satisfy the shell's fetch/XHR swaps.
    // Cross-owner links already use document navigation; leave that owner intact.
    const script = document.createElement('script');
    script.type = 'speculationrules';
    script.nonce = nonce;
    script.dataset.tpgListingPrefetch = '1';
    script.textContent = JSON.stringify({
      prefetch: [{ source: 'list', urls: [url.href], eagerness: 'immediate' }],
    });
    document.head.appendChild(script);
    speculationScript = script;
    prefetchUsed = true;
  }

  document.addEventListener('pointerover', prefetchOnIntent, { passive: true });
  document.addEventListener('focusin', prefetchOnIntent);
  document.addEventListener('submit', clearPrefetch, true);
  window.addEventListener('tpg:shell-route-will-change', clearPrefetch);
  window.TPGListingNavigationExperience = Object.freeze({ swap });
})(window, document);
